import { InputAdornment, Typography } from '@material-ui/core';
import { LocalGasStation } from '@material-ui/icons';
import { ToggleButton } from '@material-ui/lab';
import { FormikNumberField, FormikTextField } from '@superdispatch/forms';
import { Stack } from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { useFormikContext } from 'formik';
import { TowTruckIcon } from 'shared/icons';
import { BarcodeIcon } from 'shared/icons/BarcodeIcon';
import { DriveawayIcon } from 'shared/icons/DriveawayIcon';
import { TruckFaceIcon } from 'shared/icons/TruckFaceIcon';
import { WeightIcon } from 'shared/icons/WeightIcon';
import { DriverPreferencesTransportTypeDTO } from 'shared/modules/driver/DriverPreferencesDTO';
import { DriverPreferencesBanner } from '../DriverPreferencesBanner';
import { FormikToggleButtonGroup } from '../FormikToggleButtonGroup';
import { EnclosedTrailerIcon } from '../icons/DriverPreferencesEnclosedTrailerIcon';
import { OpenTrailerIcon } from '../icons/DriverPreferencesOpenTrailerIcon';

export function TransportTypeStepContent() {
  const { setTouched, values } =
    useFormikContext<DriverPreferencesTransportTypeDTO>();

  return (
    <>
      <DriverPreferencesBanner text="Specify your transport type and equipment for personalized suggestions." />

      <Box padding="small">
        <Stack space="medium" align="left">
          <FormikToggleButtonGroup
            name="transport_type"
            onChange={() => {
              void setTouched({}, false);
            }}
          >
            <ToggleButton value="truck">
              <Stack align="center">
                <TruckFaceIcon />
                <Typography variant="body1">Truck Load</Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton value="towing">
              <Stack align="center">
                <TowTruckIcon />
                <Typography variant="body1">Towing</Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton value="driveaway">
              <Stack align="center">
                <DriveawayIcon />
                <Typography variant="body1">Driveaway</Typography>
              </Stack>
            </ToggleButton>
          </FormikToggleButtonGroup>

          {(values.transport_type === 'truck' ||
            values.transport_type === 'towing') && (
            <FormikNumberField
              name="transport_fuel_expenditure_mpg"
              label="Fuel Expenditure"
              fullWidth={true}
              inputProps={{ decimalScale: 2, maxLength: 6 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocalGasStation fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography color="textSecondary" noWrap={true}>
                      <b>mpg</b>
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          )}

          {values.transport_type === 'truck' && (
            <Stack space="medium" align="left">
              <TextBox align="left" variant="heading-4">
                Trailer Details
              </TextBox>

              <FormikToggleButtonGroup name="transport_trailer_type_is_enclosed">
                <ToggleButton value={false}>
                  <Stack align="center">
                    <OpenTrailerIcon />
                    <Typography variant="body1">Open</Typography>
                  </Stack>
                </ToggleButton>
                <ToggleButton value={true}>
                  <Stack align="center">
                    <EnclosedTrailerIcon />
                    <Typography variant="body1">Enclosed</Typography>
                  </Stack>
                </ToggleButton>
              </FormikToggleButtonGroup>

              <FormikTextField
                name="transport_vin"
                label="Trailer VIN Number (Optional)"
                fullWidth={true}
                inputProps={{ maxLength: 17 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BarcodeIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <FormikNumberField
                name="transport_load_capacity_lbs"
                label="Payload Capacity"
                helperText={
                  <TextBox color="secondary" variant="caption">
                    The maximum operating weight of your trailer
                  </TextBox>
                }
                fullWidth={true}
                inputProps={{ decimalScale: 2, maxLength: 9 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WeightIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography color="textSecondary" noWrap={true}>
                        <b>lbs</b>
                      </Typography>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          )}
        </Stack>
      </Box>
    </>
  );
}
